<template>
  <div>
    <Header>Vehicle Form</Header>
    <div class="tiled-bar-image"></div>
    <Main>
      <div class="flex justify-center gap-2">
        <Button
          color="gray"
          size="md"
          @click="toVehicleList()">
          Back to Vehicle List
        </Button>
      </div>
      <Spinner
        v-if="vehicleLoading"
        message="Loading Vehicle Info"
        :size="16"
      />
      <div class="mx-auto my-4 max-w-6xl">
        <ValidationObserver v-slot="{ invalid, validate, errors, reset }">
          <form name="vehicleForm" class="relative" @submit.prevent="validate().then(() => onSubmit(invalid, reset))">
            <div class="container">
              <h2 class="font-medium text-4xl">Vehicle Info</h2>
            </div>
            <div v-if="enableVendorSelect" class="container mb-3">
              <SelectRequiredData
                v-model="formValues.requiredData"
                :includeVendorSelect="true"
                :enableVendorSelect="enableVendorSelect"
                :vendorOptions="vendorOptions"
              >
              </SelectRequiredData>
            </div>
            <div class="flex flex-column">
              <div class="container grid grid-cols-3 mb-3">
                <div class="flex flex-col gap-1">
                  <ValidationProvider rules="required|max:8|min:4" name="license plate" v-slot="{ errors }">
                    <TextField
                      class="w-auto pr-3"
                      :id="'vehicle-license-plate'"
                      v-model="formValues.licensePlate"
                      @input="formValues.licensePlate = formValues.licensePlate.toUpperCase()"
                    >
                      License Plate
                    </TextField>
                    <span
                      class="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1"
                      name="error-text">
                      {{ errors[0] }}
                    </span>
                  </ValidationProvider>
                </div>
                <div class="flex flex-col gap-1">
                  <ValidationProvider :rules="'required'" name="license state" v-slot="{ errors }">
                    <div class="flex flex-row items-center">
                      <SelectionField
                        class="!rounded-r-md"
                        v-model="formValues.licenseState"
                        :options="states"
                      >
                        License State
                      </SelectionField>
                    </div>
                    <span
                      class="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1"
                      name="error-text">
                      {{ errors[0] }}
                    </span>
                  </ValidationProvider>
                </div>
              </div>
              <div class="container mb-3">
                <div class="flex flex-col gap-1">
                  <ValidationProvider rules="required|max:17|min:17" name="VIN" v-slot="{ errors }">
                    <TextField
                      class="w-1/3 pr-3"
                      :id="'vehicle-vin'"
                      v-model="formValues.vin"
                      @input="formValues.vin = formValues.vin.toUpperCase()"
                    >
                      Vehicle identification number (VIN)
                    </TextField>
                    <span
                      class="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1"
                      name="error-text">
                      {{ errors[0] }}
                    </span>
                  </ValidationProvider>
                </div>
              </div>
              <div class="container grid grid-cols-3 mb-3">
                <div class="flex flex-col gap-1">
                  <ValidationProvider rules="required" name="make" v-slot="{ errors }">
                    <SelectionField
                      class="pr-3"
                      :id="'vehicle-make'"
                      :options="makesList"
                      v-model="formValues.make"
                      @input="(val) => updateMake(val)"
                    >
                      Make
                    </SelectionField>
                    <span
                      class="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1"
                      name="error-text">
                      {{ errors[0] }}
                    </span>
                  </ValidationProvider>
                </div>
                <div class="flex flex-col gap-1">
                  <ValidationProvider rules="required" name="model" v-slot="{ errors }">
                    <SelectionField
                      class="pr-3"
                      :id="'vehicle-model'"
                      :options="modelList"
                      :disabled="!formValues.make"
                      v-model="formValues.model"
                      @input="(val) => updateModel(val)"
                    >
                    <div class="flex flex-row items-center gap-2"
                      :title="'Request additional makes or models at technology@drivecst.org'">
                      <span>Model</span>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        height="16px"
                        viewBox="0 -960 960 960"
                        width="16px"
                        fill="#6b7280">
                        <path d="M480-240q20 0 34-14t14-34q0-20-14-34t-34-14q-20 0-34 14t-14 34q0 20 14 34t34 14Zm-36-153h73q0-37 6.5-52.5T555-485q35-34 48.5-58t13.5-53q0-55-37.5-89.5T484-720q-51 0-88.5 27T343-620l65 27q9-28 28.5-43.5T482-652q28 0 46 16t18 42q0 23-15.5 41T496-518q-35 32-43.5 52.5T444-393Zm36 297q-79 0-149-30t-122.5-82.5Q156-261 126-331T96-480q0-80 30-149.5t82.5-122Q261-804 331-834t149-30q80 0 149.5 30t122 82.5Q804-699 834-629.5T864-480q0 79-30 149t-82.5 122.5Q699-156 629.5-126T480-96Zm0-72q130 0 221-91t91-221q0-130-91-221t-221-91q-130 0-221 91t-91 221q0 130 91 221t221 91Zm0-312Z"/>
                      </svg>
                    </div>
                    </SelectionField>
                    <span
                      class="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1"
                      name="error-text">
                      {{ errors[0] }}
                    </span>
                    <span v-if="invalidModel"
                      class="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1"
                      name="error-text">
                      This model is not valid for the selected make. Please select a different model or request a new one at technology@drivecst.org
                    </span>
                  </ValidationProvider>
                </div>
                <div class="flex flex-col gap-1">
                  <ValidationProvider rules="required" name="color" v-slot="{ errors }">
                    <SelectionField
                      class="pr-3"
                      :id="'vehicle-color'"
                      v-model="formValues.color"
                      :options="colorList"
                      @input="(val) => checkValidColor(val)"
                    >
                      Color
                    </SelectionField>
                    <span
                      class="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1"
                      name="error-text">
                      {{ errors[0] }}
                    </span>
                    <span v-if="invalidColor"
                      class="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1"
                      name="error-text">
                      This color is not valid. Please select a different color or request a new one at technology@drivecst.org
                    </span>
                  </ValidationProvider>
                </div>
              </div>
              <div class="container mb-3">
                <div class="flex flex-col gap-1">
                  <ValidationProvider rules="required" name="manufacturing date" v-slot="{ errors }">
                    <CalendarField
                      class="w-1/3 pr-3"
                      :type="'month'"
                      :id="'vehicle-mfd'"
                      :max="today"
                      v-model="formValues.mfd"
                    >
                      Manufacturing Date
                    </CalendarField>
                    <span
                      class="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1"
                      name="error-text">
                      {{ errors[0] }}
                    </span>
                  </ValidationProvider>
                </div>
              </div>
              <div class="container mb-3">
                <div class="flex flex-col gap-1">
                  <ValidationProvider rules="required" name="capacity" v-slot="{ errors }">
                    <TextField
                      class="w-1/3 pr-3"
                      :type="'number'"
                      :min="'1'"
                      :id="'vehicle-capacity'"
                      v-model="formValues.capacity"
                    >
                      Capacity
                    </TextField>
                    <span
                      class="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1"
                      name="error-text">
                      {{ errors[0] }}
                    </span>
                  </ValidationProvider>
                </div>
              </div>
              <div class="container mb-3">
                <div class="flex flex-col gap-1">
                  <ValidationProvider rules="" name="camera system" v-slot="{ errors }">
                    <TextField
                      class="w-1/3 pr-3"
                      :id="'vehicle-camera-system'"
                      v-model="formValues.cameraSystem"
                    >
                      Camera System
                    </TextField>
                    <span
                      class="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1"
                      name="error-text">
                      {{ errors[0] }}
                    </span>
                  </ValidationProvider>
                </div>
              </div>
              <div class="container mb-3">
                <div class="flex flex-col gap-1">
                  <ValidationProvider rules="required" name="wheelchair positions" v-slot="{ errors }">
                    <TextField
                      class="w-1/3 pr-3"
                      :type="'number'"
                      :min="'0'"
                      :id="'vehicle-wheelchair-positions'"
                      v-model="formValues.wheelchairPositions"
                    >
                      Wheelchair Positions
                    </TextField>
                    <span
                      class="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1"
                      name="error-text">
                      {{ errors[0] }}
                    </span>
                  </ValidationProvider>
                </div>
              </div>
              <div class="container mb-3">
                <div class="flex flex-col gap-1">
                  <ValidationProvider rules="required" name="abulatory passenger capacity" v-slot="{ errors }">
                    <TextField
                      class="w-1/3 pr-3"
                      :type="'number'"
                      :min="'0'"
                      :id="'vehicle-abulatory-passenger-capacity'"
                      v-model="formValues.ambulatoryPassengerCapacity"
                    >
                      Ambulatory Passenger Capacity
                    </TextField>
                    <span
                      class="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1"
                      name="error-text">
                      {{ errors[0] }}
                    </span>
                  </ValidationProvider>
                </div>
              </div>
              <div class="container mb-3">
                <div class="flex flex-col gap-1">
                  <ValidationProvider rules="" name="enclosed" v-slot="{ errors }">
                    <label>
                      Enclosed Vehicle
                    </label>
                    <input
                      class="mx-2 pr-2 mb-2.5"
                      v-model="formValues.enclosed"
                      type="checkbox"
                    />
                    <span
                      class="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1"
                      name="error-text">
                      {{ errors[0] }}
                    </span>
                  </ValidationProvider>
                </div>
              </div>
            </div>
            <div class="container mt-4">
              <div class="w-full rounded-md bg-gray-200 p-2 my-2"
                v-if="!canSubmit">
                <span class="uppercase text-gray-500">
                  Missing Permissions to Submit Form
                </span>
              </div>
              <div class="flex flex-row">
                <span class="flex items-center font-medium tracking-wide text-red-500 text-xs mt-2 ml-1"
                  name="error-text"
                  v-if="displayFormHasErrors(errors)">
                  There are errors in fields above.
                </span>
              </div>
              <div class="flex flex-row gap-4 my-2">
                <SubmitButton
                  class="rounded-md font-medium shadow-sm"
                  type="submit"
                  color="blue"
                  size="md"
                  :disabled="!canSubmit"
                  :submitting="saving">
                  Save
                </SubmitButton>
              </div>
            </div>
          </form>
        </ValidationObserver>
      </div>
    </Main>
  </div>
</template>

<style scoped>
.tiled-bar-image {
  width: 100%;
  height: 25px;
  background-image: url("@/assets/Bar.png");
  background-repeat: repeat-x;
}
</style>

<script>
import Main from '../components/Main.vue'
import Header from '../components/Header.vue'
import Button from '../components/Button.vue'
import SubmitButton from '../components/SubmitButton.vue'

import TextField from '../components/formComponents/TextField.vue'
import CalendarField from '../components/formComponents/CalendarField.vue'
import SelectionField from '../components/formComponents/SelectionField.vue'
import Spinner from '../components/Spinner.vue'
import SelectRequiredData from '../components/SelectRequiredData.vue'

import DropdownLists from '../components/DropdownLists.json'
import vehicleFormService from '../services/pages/vehicleForm.js'
import user from '../services/user/user.js'

export default {
  components: {
    Main,
    Header,
    Button,
    SubmitButton,
    TextField,
    CalendarField,
    SelectionField,
    Spinner,
    SelectRequiredData
  },
  props: {
    vehicleId: String
  },
  data() {
    return {
      user: undefined,
      formValues: {
        enclosed: false,
        requiredData: {}
      },

      states: DropdownLists.states,
      colorList: {...DropdownLists.VehicleColorList},
      makesList: [],
      modelList: [],
      modelMap: [],
      enableVendorSelect: false,
      vehicleLoading: false,
      vehicle: undefined,
      canSubmit: true,
      saving: false,
      invalidColor: false,
      invalidModel: false,
      originalModelList: [],
      settingDefaults: false,
      today: new Date().toISOString().substring(0, 10)
    }
  },
  methods: {
    async onSubmit(formInvalid, reset) {
      let hasError = this.scrollToFirstError()
      if (formInvalid || hasError) {
        return false
      }
      this.saving = true
      let submissionPayload = {
        vendor_id: this.formValues.requiredData?.vendorId,
        vin: this.formValues.vin.toUpperCase(),
        license_plate: this.formValues.licensePlate.toUpperCase(),
        license_state: this.formValues.licenseState,
        make: this.formValues.make,
        model: this.formValues.model,
        color: this.formValues.color,
        mfd: this.formValues.mfd,
        capacity: this.formValues.capacity,
        wheelchair_positions: this.formValues.wheelchairPositions,
        camera_system: this.formValues.cameraSystem,
        ambulatory_passenger_capacity: this.formValues.ambulatoryPassengerCapacity,
        enclosed: this.formValues.enclosed
      }
      let response = undefined
      try {
        if (this.vehicle) {
          response = (await vehicleFormService.putVehicleForm(this.vehicle.id, submissionPayload))
        } else {
          response = (await vehicleFormService.postVehicleForm(submissionPayload))
          reset()
        }
      }
      catch (e) {
        console.log(e)
        return
      }
      finally {
        this.saving = false
        if(response !== undefined) {
          this.vehicle = response.data
          this.$router.replace({ name: 'VehicleForm', params: { vehicleId: this.vehicle.id } }).catch(() => {})
          this.$router.go(0)
        }
      }
    },
    scrollToFirstError() {
      const elems = document.getElementsByName('error-text');
      let hasError = false;
      for(let el of Array.from(elems)) {
        if(el.innerText && el.innerText.trim()) {
          hasError = true;
          el.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
            inline: 'center'
          });
          break;
        }
      }
      return hasError;
    },
    displayFormHasErrors(errorsMap) {
      return Object.values(errorsMap).flat().length > 0;
    },
    toVehicleList() {
      this.$router.push({
        name: 'VehicleList',
      });
    },
    async validateProvidedInfo() {
      this.vendorOptions = await vehicleFormService.getVehicleFormVendorOptionsFromCapability(this.user)

      const currentVendorIds = this.user.getView().getVendor() || []
      const currentVendorId = currentVendorIds[0]

      if (currentVendorId) {
        this.$set(
          this.formValues.requiredData,
          'vendorId',
          currentVendorId
        )
      }
      this.enableVendorSelect = this.user.can('select_vehicle_vendor')

      if (this.vehicle && !vehicleFormService.canUpdateVehicle(this.user)) {
        this.canSubmit = false
      } else if(!this.vehicle && !vehicleFormService.canSubmitNewVehicle(this.user)) {
        this.canSubmit = false
      }
    },
    checkUserCanViewPage() {
      if (!this.user.can('view_vehicle_form')) {
        this.$router.push({
          name: 'VehicleList'
        })
        return true
      }
    },
    async getVehicleModels() {
      try {
        this.modelMap = (await vehicleFormService.getVehicleModelList()).data
      }
      catch (e) {
        console.log(e)
      }
      this.makesList = this.modelMap.flatMap(make => make.make)
    },
    async setDefaultValues() {
      if (this.vehicleId !== undefined) {
        try {
          this.vehicleLoading = true
          this.vehicle = (await vehicleFormService.getVehicleDetail(this.vehicleId)).data
          this.vehicleLoading = false
        }
        catch {
          this.toVehicleList()
          return
        }
      }
      if (this.vehicle) {
        if (this.vehicle.vendor.id) {
          this.$set(this.formValues.requiredData, 'vendorId', this.vehicle.vendor.id)
        }
        if (this.vehicle.license_plate) {
          this.$set(this.formValues, 'licensePlate', this.vehicle.license_plate.toUpperCase())
        }
        if (this.vehicle.license_state) {
          this.$set(this.formValues, 'licenseState', this.vehicle.license_state)
        }
        if (this.vehicle.color) {
          this.checkValidColor(this.vehicle.color.toLowerCase())
          this.$set(this.formValues, 'color', this.vehicle.color.toLowerCase())
        }
        if (this.vehicle.vin) {
          this.$set(this.formValues, 'vin', this.vehicle.vin.toUpperCase())
        }
        if (this.vehicle.capacity) {
          this.$set(this.formValues, 'capacity', this.vehicle.capacity)
        }
        if (this.vehicle.mfd) {
          this.$set(this.formValues, 'mfd', this.vehicle.mfd.slice(0,7))
        }
        if (this.vehicle.enclosed) {
          this.$set(this.formValues, 'enclosed', this.vehicle.enclosed)
        }
        if (this.vehicle.wheelchair_positions != undefined) {
          this.$set(this.formValues, 'wheelchairPositions', this.vehicle.wheelchair_positions)
        }
        if (this.vehicle.camera_system) {
          this.$set(this.formValues, 'cameraSystem', this.vehicle.camera_system)
        }
        if (this.vehicle.ambulatory_passenger_capacity != undefined) {
          this.$set(this.formValues, 'ambulatoryPassengerCapacity', this.vehicle.ambulatory_passenger_capacity)
        }

      }
    },
    checkValidColor(color){
      if(!(Object.values(DropdownLists.VehicleColorList).includes(color) || Object.keys(DropdownLists.VehicleColorList).includes(color))){
        this.colorList[color] = color
        this.invalidColor = true
      } else {
        this.invalidColor = false
      }
    },
    setDefaultMakeModel(){
      if(!this.vehicle) return
      this.settingDefaults = true
      if (this.vehicle.make) {
        const matchedMakeEntry = this.modelMap.find(entry => entry.make.toLowerCase() === this.vehicle.make.toLowerCase())
        if (matchedMakeEntry) {
          this.$set(this.formValues, 'make', matchedMakeEntry.make)
          this.originalModelList = [...matchedMakeEntry.models]
        } else {
          if (!this.makesList.some(m => m.toLowerCase() === this.vehicle.make.toLowerCase())) {
            this.makesList = [...this.makesList, this.vehicle.make]
          }
          this.$set(this.formValues, 'make', this.vehicle.make)
          this.originalModelList = []
        }
      }
      if (this.vehicle.model) {
        const matchedModel = this.originalModelList.find(m => m.toLowerCase() === this.vehicle.model.toLowerCase())
        if (matchedModel) {
          this.modelList = [...this.originalModelList]
          this.$set(this.formValues, 'model', matchedModel)
          this.invalidModel = false
        } else {
          this.modelList = [...this.originalModelList, this.vehicle.model]
          this.$set(this.formValues, 'model', this.vehicle.model)
          this.invalidModel = true
        }
      } else {
        this.modelList = [...this.originalModelList]
      }
      this.$nextTick(() => { this.settingDefaults = false })
    },
    checkValidModel(model){
      if(this.vehicle && this.vehicle.make.toLowerCase() === this.formValues.make.toLowerCase()){
        const isValid = this.originalModelList.some(m => m.toLowerCase() === model.toLowerCase())
        if(!isValid){
          this.invalidModel = true
          if(!this.modelList.some(m => m.toLowerCase() === model.toLowerCase())){
            this.modelList = [...this.modelList, model]
          }
        } else {
          this.invalidModel = false
        }
      }
    },
    updateMake(value) {
      if (this.settingDefaults) return
      this.$set(this.formValues, 'make', value)
      this.invalidModel = false
      if(this.formValues.make){
        this.modelList = this.modelMap.find(make => make.make.toLowerCase() == this.formValues.make.toLowerCase())?.models || []
      } else {
        this.modelList = []
      }
      this.$forceUpdate()
    },
    updateModel(value) {
      if (this.settingDefaults) return
      if(this.vehicle){
        this.checkValidModel(value)
      }
      this.$set(this.formValues, 'model', value)
      this.$forceUpdate()
    }
  },
  async created() {
    this.user = await user.get()
    if (this.checkUserCanViewPage()) {
      return
    }
    await this.getVehicleModels()
    await this.setDefaultValues()
    this.setDefaultMakeModel()

    this.validateProvidedInfo()
  },
}
</script>
